/* @flow */

import moment from 'moment';
import { errorHandlerService, userStore } from 'services/ServiceRegistry';

import { LicenseTypes } from 'forms/LicenseRequestForm/LicenseRequestForm';
import { NotificationEventTypes } from 'views/Notifications/NotificationPanel/NotificationPanel';

const str = 'lkjlkj';
export const SENT_REQUEST_TYPES = {
  str,
  ASSET_FAULT_REPORT: 'Asset Fault Report',
  ASSET_QUOTE: 'Quote Request',
  ASSET_RENTAL_EXTENSION: 'Request Asset Rental Extension',
  ASSET_ALLOCATE_TO_PROJECT: 'Request Asset Allocation To Project',
  ASSET_CALIBRATION: 'Asset Calibration Request',
  ASSET_OWNERSHIP: 'Request Asset Ownership',
  CONTRACT_EXTENSION: 'Request Contract Extension',
  SUPPORT_REQUEST: 'Request Support',
  CONSIGNMENT_SALE: 'Consignment Sale Opportunity',
  SUBRENTAL_PROGRAM: 'Sub-rental Program Opportunity',
  ASSET_REQUEST: 'New Asset Request',
  RUSH_ASSET_REQUEST: 'New Rush Asset Request',
  NEW_LICENSE_REQUEST: 'New License Request',
};
Object.freeze(SENT_REQUEST_TYPES);

export const EVENT_TYPES = {
  SUPPORT_REQUEST: 'SupportRequest',
  CONSIGNMENT_SALE: 'ConsignmentSaleOpportunityRequest',
  SUBRENTAL_PROGRAM: 'Sub-rentalProgramOpportunityRequest',
  ASSET_REQUEST: 'NewAssetRequest',
  RUSH_ASSET_REQUEST: 'NewRushAssetRequest',
  ASSET_CALIBRATION_DUE: 'AssetCalibrationDue',
  CONTRACT_DUE: 'ContractDue',
  PROJECT_DUE: 'ProjectDue',
  CONTRACT_ENDED: 'ContractEnded',
  NEW_ASSET_REQUEST: 'NewAssetRequest',
  ASSET_EXTENSION_REQUEST_APPROVED: 'AssetRentalExtensionApprove',
  ASSET_EXTENSION_REQUEST_DECLINED: 'AssetRentalExtensionDecline',
  CONTRACT_EXTENSION_REQUEST_APPROVED: 'ContractExtensionApproved',
  CONTRACT_EXTENSION_REQUEST_DECLINED: 'ContractExtensionDeclined',
  NEW_LICENSE_REQUEST: 'NewLicenseRequest',
  TRANSFER_LICENSE_REQUEST: 'TransferLicenseRequest',
  REMOVE_LICENSE_REQUEST: 'RemoveLicenseRequest',
};
Object.freeze(EVENT_TYPES);

export const resolveCorrectLicenseRequestAction = (sentRequest: Object) => {
  switch (sentRequest.details.asset_request.license_request_type) {
    case LicenseTypes.REQUEST_NEW_LICENSE:
      return EVENT_TYPES.NEW_LICENSE_REQUEST;

    case LicenseTypes.TRANSFER_EXISTING_LICENSE:
      return EVENT_TYPES.TRANSFER_LICENSE_REQUEST;

    case LicenseTypes.REMOVE_CURRENT_LICENSE:
      return EVENT_TYPES.REMOVE_LICENSE_REQUEST;
  }
};

/**
 * Get event category
 * @param notification
 * @returns {string}
 */
export const getEventCategory = (notification): string => {
  if (notification.event_type === NotificationEventTypes.ASSET_RENTAL_EXTENSION_REQUEST) {
    return 'request_rental_extension';
  }

  return notification.event_category.toLowerCase();
};

/**
 * Generate action URL for notifications list
 */
export const generateActionUrl: Function = (action: Object) => {
  const params = action.params || action.query;

  switch (action.namespace) {
    case 'notification':
      return `notifications/${params.event_id}`;
    case 'project':
      return `projects/${params.project_id}`;
    case 'contract':
      return `orders/${params.contract_id}`;
    case 'account':
      return `accounts/${params.account_id}`;
    case 'user':
      return `accounts/${params.account_id}/user/${params.user_id}`;
    case 'asset':
      return `assets/${params.asset_id ? params.asset_id : ''}`;
    case 'userManagement':
      return `users/profile/${params.user_id ? params.user_id : ''}`;

    case 'manufacturer': {
      const suffix = params.manufacturer_id ? `manufacturer/${params.manufacturer_id}` : '';

      return `assets/${suffix}`;
    }

    case 'product': {
      const suffix = params.product_id ? `product/${params.product_id}` : '';

      return `assets/${suffix}`;
    }

    case 'location': {
      const suffix =
        params.country_id && params.location_id
          ? `/${params.country_id}/${params.location_id}`
          : '';

      return `accounts/${params.account_id}/location/${params.company_id}${suffix}`;
    }

    default:
      return '/';
  }
};

/**
 * Sort array by date
 */
export const sortByDate = (arr, key: string = 'date'): Array<Object> => {
  let results = arr.data || arr;

  return results.sort((a: Object, b: Object) => {
    const dateA: Date = new Date(a[key]);
    const dateB: Date = new Date(b[key]);

    return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
  });
};

export const getSentRequestTitle = (notification: Object, suffix: string = ''): string => {
  return notification.action_name;
};

/**
 * Will create 2 objects that are used for translation in the Header of a Sent Request.
 * This function is required to map data in same fashion as in notification object.
 */
export const generateValues = (sentRequest: Object) => {
  try {
    const notification = {
      event_type: sentRequest.action_name.replace(/ /g, '').replace(/Request/, '') + 'Request',
    };

    switch (notification.event_type) {
      case 'AssetFaultReportRequest':
        notification.event_type = 'AssetFaultReported';
        break;
      case 'QuoteRequest':
        notification.event_type = 'AssetQuoteRequest';
        break;
      case 'ContractExtensionRequest':
        notification.event_type = 'ContractAssetsExtendRequest';
        break;
      case 'SupportRequest':
        return {
          notification,
          values: {
            initiated_by: sentRequest.user_name,
          },
        };
      case EVENT_TYPES.ASSET_REQUEST:
        return {
          notification,
          values: {
            id: sentRequest.details.asset_request.unique_asset_request_id,
            initiated_by: sentRequest.user_name,
            account_name: sentRequest.account_name,
          },
        };
      case EVENT_TYPES.RUSH_ASSET_REQUEST:
        return {
          notification,
          values: {
            id: sentRequest.details.asset_request.unique_asset_request_id,
            initiated_by: sentRequest.user_name,
            account_name: sentRequest.account_name,
          },
        };
      case EVENT_TYPES.SUBRENTAL_PROGRAM:
        return {
          notification,
          values: {
            initiated_by: sentRequest.user_name,
          },
        };
      case EVENT_TYPES.CONSIGNMENT_SALE:
        return {
          notification,
          values: {
            initiated_by: sentRequest.user_name,
          },
        };

      case EVENT_TYPES.NEW_LICENSE_REQUEST: {
        if (sentRequest.details && sentRequest.details.asset_request) {
          notification.event_type = resolveCorrectLicenseRequestAction(sentRequest);

          return {
            notification,
            values: {
              account_name: sentRequest.account_name,
              user_name: sentRequest.user_name,
              request_id: sentRequest.details.asset_request.unique_license_request_id,
            },
          };
        }
      }
      default:
    }

    const { details } = sentRequest;
    const { contract } = details;

    // values are mapped by names to translation string in json file.
    const values: Object = {
      initiated_by: sentRequest.user_name,
      account_name: details.account_name,
      account_number: details.account_number,
      project_name:
        sentRequest.details.project_details && sentRequest.details.project_details.new_project_name,
    };

    if (contract) {
      notification.template_values = {
        contract_id: contract.id,
        contract_number: contract.er_contract_number,
      };

      const [asset] = contract.assets;
      Object.assign(values, {
        asset_number: asset.asset_number,
        contract_number: contract.er_contract_number,
        assets: asset.asset_number,
        manufacturer_name: asset.manufacturer,
        model: asset.model,
      });
    }

    return { values, notification };
  } catch (err) {
    // TODO: This exception has to be handled differently as returning empty objects will break
    // other parts of the application. Perhaps redirect to 500 or 404?
    errorHandlerService.handleException(err);
    return { values: {}, notification: {} };
  }
};

/**
 * Maps notification to copy in json file
 * TODO: improve setting of isAsset and isRental
 */
export const getNotificationCopy = (
  notification: Object,
  suffix: string = ''
): { key: string, options?: Object } => {
  const isSentRequest = notification.hasOwnProperty('action_name');

  try {
    switch (notification.event_type) {
      case EVENT_TYPES.SUPPORT_REQUEST:
      case EVENT_TYPES.CONSIGNMENT_SALE:
      case EVENT_TYPES.SUBRENTAL_PROGRAM:
      case EVENT_TYPES.ASSET_REQUEST:
      case EVENT_TYPES.RUSH_ASSET_REQUEST:
      case EVENT_TYPES.CONTRACT_ENDED:
        return { key: `general:notifications.${notification.event_type}${suffix}` };
    }

    if (isSentRequest) {
      return { key: `general:notifications.${notification.action_name}` };
    }

    const isAsset =
      typeof notification.event_type === 'string' && notification.event_type.includes('Asset');
    const isRental =
      !!notification.template_values &&
      (notification.template_values.contract_number || notification.template_values.contract_id);

    let prefix = '';
    if (isAsset) {
      prefix = isRental ? 'Rental' : 'Owned';
    }

    let _suffix = suffix;
    const options = {};

    if (
      notification.event_type === EVENT_TYPES.CONTRACT_DUE ||
      notification.event_type === EVENT_TYPES.PROJECT_DUE ||
      notification.event_type === EVENT_TYPES.ASSET_CALIBRATION_DUE
    ) {
      const daysInterval = Number(notification.template_values.days_to_due_date);

      options.count = daysInterval;
    }

    if (
      _suffix.includes('Body') &&
      (notification.event_type === EVENT_TYPES.ASSET_EXTENSION_REQUEST_APPROVED ||
        notification.event_type === EVENT_TYPES.ASSET_EXTENSION_REQUEST_DECLINED ||
        notification.event_type === EVENT_TYPES.CONTRACT_EXTENSION_REQUEST_APPROVED ||
        notification.event_type === EVENT_TYPES.CONTRACT_EXTENSION_REQUEST_DECLINED) &&
      userStore.activeUser &&
      notification.template_values &&
      notification.template_values.requester_id === userStore.activeUser.id
    ) {
      _suffix = `Your${_suffix}`;
    }

    return {
      key: `general:notifications.${prefix}${notification.event_type}${_suffix}`,
      options,
    };
  } catch (err) {
    errorHandlerService.handleException(err);
    return { key: '' };
  }
};

/**
 * Recursively filters array by search string.
 */
export const replaceParamsInString = (str: string, params: Object) => {
  let s = str;

  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      s = s.split('{' + key + '}').join(params[key]);
    }
  }

  return s;
};

/**
 * Recursively filters array by search string
 */
export const filterNotificationsBySearch = (
  arr: Array<Object>,
  search: string = '',
  t: Function
) => {
  if (search) {
    const chars = /\b[,]|\s[-]|^\s+|\s+$/gi;
    const searchTerms = search.replace(chars, '');

    return arr.filter(
      (notification: Object): boolean => {
        const titleInfo = getNotificationCopy(notification, 'Title');
        const title = t(titleInfo.key, {
          ...notification,
          ...notification.template_values,
          ...titleInfo.options,
          ...notification.action_name,
        });

        const bodyInfo = getNotificationCopy(notification, 'Body');
        const body = t(bodyInfo.key, {
          ...notification,
          ...notification.template_values,
          ...bodyInfo.options,
          ...notification.action_name,
        });
        const content: string = title + body;
        const regex: RegExp = new RegExp(searchTerms, 'gi');

        return content.search(regex) >= 0;
      }
    );
  }

  return arr;
};

/**
 * Check each template values that are dates and
 * convert them to a human friendly format.
 */
export const parseTemplateDates = (templateValues: Object) => {
  Object.keys(templateValues).forEach((key: string) => {
    if (key.search('date') >= 0) {
      const value = templateValues[key];

      templateValues[key] = moment(value).format('MMMM DD, YYYY');
    }
  });

  return templateValues;
};
